import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import ReactPaginate from "react-paginate";
import { useNavigate } from "react-router-dom";
import useFetchClasses from "../../hooks/useFetchClasses";
import { ApiUrl } from "../../../ApiUrl";
import "./AdmViewFeeStructure.css";

const newExistingOptions = [
  { value: "", label: "All" },
  { value: "N", label: "New" },
  { value: "E", label: "Existing" },
];

const freezeOptions = [
  { value: "", label: "All" },
  { value: "Y", label: "Frozen" },
  { value: "N", label: "Not Frozen" },
];

const toSelectOptions = (items, valueKey, labelKey) =>
  items.map((item) => ({
    value: item[valueKey],
    label: item[labelKey],
  }));

const AdmViewFeeStructure = () => {
  const navigate = useNavigate();
  const {
    classes,
    loading: classLoading,
    error: classError,
  } = useFetchClasses();

  const [feeStructures, setFeeStructures] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedNewExisting, setSelectedNewExisting] = useState(
    newExistingOptions[0],
  );
  const [selectedFreeze, setSelectedFreeze] = useState(freezeOptions[0]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const totalPages = Math.ceil(feeStructures.length / rowsPerPage);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = feeStructures.slice(indexOfFirstRow, indexOfLastRow);

  const classOptions = useMemo(
    () => toSelectOptions(classes || [], "id", "classname"),
    [classes],
  );

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${ApiUrl.apiurl}CATEGORY/GetAllCategory/`);
        const result = await response.json();
        if (result.message === "success" || result.message === "Success") {
          setCategoryOptions(
            (result.data || []).map((category) => ({
              value: category.id,
              label: category.categoryname || category.category_name,
            })),
          );
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  const fetchFeeStructures = async () => {
    const academicSessionId = localStorage.getItem("academicSessionId");
    const orgId = localStorage.getItem("orgId");
    const branchId = localStorage.getItem("branchId");

    if (!academicSessionId) {
      alert("Please select an academic session from the navbar before searching.");
      return;
    }

    const params = new URLSearchParams({
      academic_year_id: academicSessionId,
    });

    if (orgId) params.append("org_id", orgId);
    if (branchId) params.append("branch_id", branchId);
    if (selectedClass?.value) params.append("class_id", selectedClass.value);
    if (selectedCategory?.value) {
      params.append("category_id", selectedCategory.value);
    }
    if (searchText.trim()) params.append("search", searchText.trim());

    try {
      setLoading(true);
      setHasSearched(true);
      setCurrentPage(1);
      const response = await fetch(
        `${ApiUrl.apiurl}FeeStructure/Search/?${params.toString()}`,
      );
      const result = await response.json();

      if (response.ok && result.message === "Success") {
        let data = result.data || [];
        if (selectedNewExisting?.value) {
          data = data.filter(
            (item) => item.new_existing === selectedNewExisting.value,
          );
        }
        if (selectedFreeze?.value) {
          data = data.filter((item) => item.enabled === selectedFreeze.value);
        }
        setFeeStructures(data);
      } else {
        setFeeStructures([]);
      }
    } catch (error) {
      console.error("Error fetching fee structures:", error);
      setFeeStructures([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected + 1);
  };

  useEffect(() => {
    fetchFeeStructures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (feeStructure) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${feeStructure.fee_structure_desc}"?`,
    );
    if (!confirmed) return;

    try {
      const userId = sessionStorage.getItem("userId") || 1;
      const response = await fetch(
        `${ApiUrl.apiurl}FeeStructure/deleteFeeStructure/${feeStructure.id}/`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updated_by: userId }),
        },
      );
      const result = await response.json();
      if (response.ok) {
        alert(result.message || "Fee structure deleted successfully.");
        fetchFeeStructures();
      } else {
        alert(result.message || "Unable to delete fee structure.");
      }
    } catch (error) {
      console.error("Error deleting fee structure:", error);
      alert("Unable to delete fee structure.");
    }
  };

  return (
    <div className="view-fee-structure">
      <div className="row mb-3 mx-0">
        <div className="col-12 mb-3 mt-3 d-flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-primary me-2"
            style={{ width: "150px" }}
            onClick={fetchFeeStructures}
            disabled={loading}
          >
            Search
          </button>
          <button
            type="button"
            className="btn btn-danger me-2"
            style={{ width: "150px" }}
            onClick={() => navigate("/admin/dashboard")}
          >
            Close
          </button>
        </div>
      </div>

      <div className="row mt-2 mx-2">
        <div className="col-12 custom-section-box">
          <div className="row">
            <div className="col-12 col-md-3 mb-3">
              <label htmlFor="fee-structure-search" className="form-label">
                Search
              </label>
              <input
                id="fee-structure-search"
                type="text"
                className="form-control detail"
                placeholder="Code or description"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
              />
            </div>

            <div className="col-12 col-md-3 mb-3">
              <label htmlFor="fee-structure-class" className="form-label">
                Class
              </label>
              <Select
                id="fee-structure-class"
                classNamePrefix="react-select"
                className="detail"
                options={classOptions}
                value={selectedClass}
                placeholder={
                  classLoading
                    ? "Loading classes..."
                    : classError
                    ? "Unable to load classes"
                    : "All Classes"
                }
                isClearable
                isDisabled={classLoading || Boolean(classError)}
                onChange={setSelectedClass}
              />
            </div>

            <div className="col-12 col-md-3 mb-3">
              <label htmlFor="fee-structure-category" className="form-label">
                Category
              </label>
              <Select
                id="fee-structure-category"
                classNamePrefix="react-select"
                className="detail"
                options={categoryOptions}
                value={selectedCategory}
                placeholder="All Categories"
                isClearable
                onChange={setSelectedCategory}
              />
            </div>

            <div className="col-12 col-md-3 mb-3">
              <label htmlFor="fee-structure-type" className="form-label">
                New/Existing
              </label>
              <Select
                id="fee-structure-type"
                classNamePrefix="react-select"
                className="detail"
                options={newExistingOptions}
                value={selectedNewExisting}
                onChange={(option) =>
                  setSelectedNewExisting(option || newExistingOptions[0])
                }
              />
            </div>

            <div className="col-12 col-md-3 mb-3">
              <label htmlFor="fee-structure-freeze" className="form-label">
                Freeze Status
              </label>
              <Select
                id="fee-structure-freeze"
                classNamePrefix="react-select"
                className="detail"
                options={freezeOptions}
                value={selectedFreeze}
                onChange={(option) => setSelectedFreeze(option || freezeOptions[0])}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row mt-4 mx-2">
        <div className="col-12">
          {loading ? (
            <div className="view-fee-structure-empty">Loading fee structures...</div>
          ) : feeStructures.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-bordered view-fee-structure-table">
                <thead>
                  <tr>
                    <th>Sr. No.</th>
                    <th>Academic Session</th>
                    <th>Fee Structure Code</th>
                    <th>Fee Structure Description</th>
                    <th>Class</th>
                    <th>Category</th>
                    <th>New/Existing</th>
                    <th>Version</th>
                    <th>Freeze</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRows.map((item, index) => (
                    <tr key={item.id}>
                      <td>{indexOfFirstRow + index + 1}</td>
                      <td>{item.academic_session || "-"}</td>
                      <td>{item.fee_structure_code || "-"}</td>
                      <td>{item.fee_structure_desc || "-"}</td>
                      <td>{item.classname || "-"}</td>
                      <td>{item.category_name || "-"}</td>
                      <td>
                        {item.new_existing === "N"
                          ? "New"
                          : item.new_existing === "E"
                          ? "Existing"
                          : "-"}
                      </td>
                      <td>{item.version_no || "-"}</td>
                      <td>{item.enabled === "Y" ? "Yes" : "No"}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-danger"
                          style={{ width: "120px" }}
                          onClick={() => handleDelete(item)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="view-fee-structure-empty">
              {hasSearched
                ? "No fee structures found for the selected filters."
                : "Use Search to view fee structures."}
            </div>
          )}
          {feeStructures.length > 0 && (
            <ReactPaginate
              previousLabel={"Previous"}
              nextLabel={"Next"}
              breakLabel={"..."}
              breakClassName={"page-item"}
              breakLinkClassName={"page-link"}
              pageCount={totalPages}
              marginPagesDisplayed={2}
              pageRangeDisplayed={5}
              onPageChange={handlePageChange}
              containerClassName={"pagination justify-content-center"}
              pageClassName={"page-item"}
              pageLinkClassName={"page-link"}
              previousClassName={"page-item"}
              previousLinkClassName={"page-link"}
              nextClassName={"page-item"}
              nextLinkClassName={"page-link"}
              activeClassName={"active"}
              forcePage={currentPage - 1}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdmViewFeeStructure;
